import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { checkSessionExpiration, resetInactivityTimer } from './sessionManager.js';
import AuthService from '../Security/AuthService.js';
import PatientInfoService from '../patientInfo/patientInfoService.js';
import '../loginServices/loginStyle.css';

const authService = new AuthService();
const patientService = new PatientInfoService();

const LoginPatient = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [message, setMessage] = useState('');
  const [messageStyle, setMessageStyle] = useState('');
  const [spinnerVisible, setSpinnerVisible] = useState(false);

  // Forgot password mode
  const [isResetMode, setIsResetMode] = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    checkSessionExpiration();
  }, []);

  const handleLoginSubmit = async (event) => {
    event.preventDefault();
    setSpinnerVisible(true);
    setIsLoggingIn(true);
    setMessage('');

    const loginRequest = { username, password };

    try {
      // Step 1: Authenticate user (check if patient credentials valid)
      await patientService.login(loginRequest);

      // Step 2: Login with shared account to get JWT
      const authResult = await authService.login({
        username: "PatientAccount",
        password: "patientofnewu"
      });

      // Step 3: Fetch patient info to get patientId
      const patientData = await patientService.getPatientByUsername(username);

      // Step 4: Store in localStorage
      localStorage.setItem("jwt", authResult.token);
      localStorage.setItem("role", "PATIENT");
      localStorage.setItem("username", username);
      localStorage.setItem("patientId", patientData.patientId);
      sessionStorage.setItem("sessionExpires", Date.now() + 30 * 60 * 1000);

      setMessage("Login successful");
      setMessageStyle("green");
      resetInactivityTimer();

      setTimeout(() => navigate("/dashboard/patient"), 1500);
    } catch (error) {
      console.error("Login failed:", error);
      setMessage("Login failed. Please check your credentials.");
      setMessageStyle("red");
    } finally {
      setSpinnerVisible(false);
      setIsLoggingIn(false);
    }
  };

  const handleRequestReset = async () => {
    setMessage("Requesting reset code...");
    setMessageStyle("black");
    try {
      await patientService.requestResetCode(username);
      setMessage("Verification code sent to your email.");
      setMessageStyle("green");
      setIsResetMode(true);
    } catch (err) {
      console.error(err);
      setMessage(err.message);
      setMessageStyle("red");
    }
  };

  const handleResetPassword = async () => {
    setMessage("Resetting password...");
    setMessageStyle("black");
    try {
      await patientService.verifyResetCodeAndChangePassword({
        username,
        code: resetCode,
        newPassword
      });

      const authResult = await authService.login({
        username: "PatientAccount",
        password: "patientofnewu"
      });

      const patientData = await patientService.getPatientByUsername(username);

      localStorage.setItem("jwt", authResult.token);
      localStorage.setItem("role", "PATIENT");
      localStorage.setItem("username", username);
      localStorage.setItem("patientId", patientData.patientId);
      sessionStorage.setItem("sessionExpires", Date.now() + 30 * 60 * 1000);

      setMessage("Password reset successful. Logging in...");
      setMessageStyle("green");
      resetInactivityTimer();

      setTimeout(() => navigate("/dashboard/patient"), 1500);
    } catch (err) {
      console.error(err);
      setMessage(err.message || "Failed to reset password.");
      setMessageStyle("red");
    }
  };

  return (
    <div className="login-container">
      <form className="login-form" onSubmit={handleLoginSubmit}>
        <input
          type="text"
          placeholder="Username or Email"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          className="input-field"
        />

        {!isResetMode ? (
          <>
            <div className="password-container">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-field"
              />
              <label className="show-password">
                <input
                  type="checkbox"
                  checked={showPassword}
                  onChange={() => setShowPassword(!showPassword)}
                  className="checkbox"
                />
                Show Password
              </label>
            </div>

            <button type="submit" className="login-button" disabled={isLoggingIn}>
              {isLoggingIn ? 'Logging in...' : 'Login'}
            </button>

            <button
              type="button"
              className="link-button"
              onClick={handleRequestReset}
              disabled={!username}
            >
              Forgot Password?
            </button>
          </>
        ) : (
          <>
            <input
              type="text"
              placeholder="Verification Code"
              value={resetCode}
              onChange={(e) => setResetCode(e.target.value)}
              className="input-field"
            />
            <input
              type="password"
              placeholder="New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="input-field"
            />
            <button
              type="button"
              className="login-button"
              onClick={handleResetPassword}
              disabled={!resetCode || !newPassword}
            >
              Reset Password & Login
            </button>
          </>
        )}

        {spinnerVisible && <div className="spinner"></div>}
      </form>

      {message && <div className="message" style={{ color: messageStyle }}>{message}</div>}
    </div>
  );
};

export default LoginPatient;
